import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  Button,
  CardMedia,
  Box,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Fade,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material'
import { FC, useState, useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'

import {
  selectScanner,
  clearCurrentScan,
  updateScanSettings,
  clearError,
  mergeValidatedSettings,
  setCurrentScanAfterRotation,
} from '@state/reducers/scannerReducer'
import { useIntl } from 'react-intl'
import {
  startScannerService,
  discoverScanners,
  scanDocument,
  getScannerCapabilities,
} from '@state/thunks/ScannerThunk'
import { FileTypeEnum } from '../../common/interfaces'
import { scannerService, ScanSettings } from '@services/ScannerService'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import TimelineDot from '@mui/lab/TimelineDot'
import { mergeSettings } from '../../common/ScannerSettingValidators'
import { rotateScannerImages } from '@state/thunks/ImagesThunk'
import { Rotate90DegreesCw, RotateRight } from '@mui/icons-material'

type Props = {
  setScannerState: (
    state: 'error' | 'scanning' | 'idle' | 'ready' | 'searching',
  ) => void
  savePfd?: (file: File) => void
  fileType: string
  setFileType: (type: FileTypeEnum) => void
  fileoptions: string[]
}

const DocumentsScanner: FC<Props> = ({
  setFileType,
  fileType,
  fileoptions,
  setScannerState,
  savePfd,
}) => {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const scanners = useAppSelector((state) => state.scanner.scanners)
  const selectedScannerId = useAppSelector(
    (state) => state.scanner.selectedScannerId,
  )
  const isScanning = useAppSelector((state) => state.scanner.isScanning)
  const isDiscovering = useAppSelector((state) => state.scanner.isDiscovering)
  const currentScan = useAppSelector((state) => state.scanner.currentScan)
  const scanError = useAppSelector((state) => state.scanner.scanError)
  const discoveryError = useAppSelector((state) => state.scanner.discoveryError)
  const serviceConnected = useAppSelector(
    (state) => state.scanner.serviceConnected,
  )
  const isScannerCapsLoaded = useAppSelector(
    (state) => state.scanner.loadScannerCaps,
  )
  const scannerCaps = useAppSelector((state) => state.scanner.scannerCaps)
  const validatedSettings = useAppSelector(
    (state) => state.scanner.validatedSettings,
  )
  const settingsWarnings = useAppSelector((state) => state.scanner.capsWarnings)

  const currentSettings = useAppSelector((state) => state.scanner.scanSettings)

  const [selectedPageSize, setSelectedPageSize] = useState<string>('A4')
  const [isValidating, setIsValidating] = useState(false)
  const [rotationDegree, setRotationDegree] = useState<number>(0)

  const pageSize = [
    { id: '1', size: 'A4' },
    { id: '11', size: 'Letter' },
    { id: '111', size: 'Legal' },
  ]

  const handleRotation = () => {
    setRotationDegree((prev) => (prev + 90) % 360)
  }

  useEffect(() => {
    dispatch(discoverScanners())
    dispatch(startScannerService())
  }, [dispatch])

  useEffect(() => {
    if (selectedScannerId) {
      dispatch(getScannerCapabilities(selectedScannerId))
    }
  }, [selectedScannerId, dispatch])

  useEffect(() => {
    const validateSettings = async () => {
      if (selectedScannerId && fileType) {
        setIsValidating(true)
        try {
          const validation = mergeSettings({
            Caps: scannerCaps,
            documentType: fileType as FileTypeEnum,
          })
          dispatch(mergeValidatedSettings({ validation, fileType }))
        } catch (error) {
          console.log(`Scanner Validation Failed ${error}`)
          dispatch(
            enqueueSnackbar({
              message: `Scanner Validation Failed ${error}`,
              type: 'false',
            }),
          )
        } finally {
          setIsValidating(false)
        }
      }
    }
    validateSettings()
  }, [selectedScannerId, fileType, dispatch, scannerCaps])

  const availableOptions = {
    dpiValues: scannerCaps?.dpiValues ?? [150, 200, 300, 400, 600],
    ColorModes: scannerCaps?.bitDepthValues ?? [
      'Color',
      'Grayscale',
      'BlackAndWhite',
    ],
    paperSources: [
      ...(scannerCaps?.supportsFlatbed ? ['Flatbed'] : []),
      ...(scannerCaps?.supportsFeeder ? ['Feeder'] : []),
      ...(scannerCaps?.supportsDuplex ? ['Duplex'] : []),
    ],
  }

  if (availableOptions.paperSources.length === 0) {
    availableOptions.paperSources = ['Feeder', 'Flatbed', 'Duplex']
  }

  useEffect(() => {
    if (isScanning) {
      setScannerState('scanning')
    } else if (isDiscovering) {
      setScannerState('searching')
    } else if (scanError || discoveryError) {
      setScannerState('error')
    } else if (selectedScannerId) {
      setScannerState('ready')
    } else {
      setScannerState('idle')
    }
  }, [
    isScanning,
    isDiscovering,
    scanError,
    discoveryError,
    selectedScannerId,
    setScannerState,
  ])

  const handleRefreshScanners = async () => {
    try {
      await dispatch(discoverScanners()).unwrap()
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: `échec de la découverte du scanner ${error}`,
          type: 'false',
        }),
      )
    }
  }

  const handleScan = async () => {
    if (!selectedScannerId) {
      alert('Veuillez sélectionner un scanner')
      return
    }
    try {
      await dispatch(
        scanDocument({
          deviceId: selectedScannerId,
          settings: {
            dpi: currentSettings.dpi,
            colorMode: currentSettings.colorMode,
            format: currentSettings.format,
            paperSource: currentSettings.paperSource,
            pageSize: currentSettings.pageSize,
            skipBlankPages:
              currentSettings.paperSource === 'duplex'
                ? true
                : currentSettings.skipBlankPages,
            blankThreshold: currentSettings.skipBlankPages
              ? 99
              : currentSettings.blankThreshold,
            compression: currentSettings.compression,
          },
        }),
      ).unwrap()
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: `échec de la découverte du scanner ${error}`,
          type: 'false',
        }),
      )
    }
  }
  const handleClearScan = () => {
    dispatch(clearCurrentScan())
  }

  const handleValidateRotation = async () => {
    const stored = currentScan?.rawImages
    if (!stored) return

    const images: string[] = stored

    try {
      const result = await dispatch(
        rotateScannerImages({ images, angle: rotationDegree }),
      ).unwrap()

      if (!result.success) {
        dispatch(
          enqueueSnackbar({
            message: `Échec de la rotation pour ${result.failedIndices.length} image(s)`,
            type: 'false',
          }),
        )
        return
      }

      let blob: Blob
      let extension: string

      if (result.images.length === 1) {
        blob = scannerService.base64ToBlob(result.images[0], 'image/jpeg')
        extension = 'jpg'
      } else {
        const pdfResponse = await fetch(
          `${process.env.REACT_APP_SCANNER_SERVICE_URL}/api/convert/pdf`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ images: result.images }),
          },
        )
        if (!pdfResponse.ok) {
          throw new Error('PDF conversion failed')
        }
        const pdfData = await pdfResponse.json()
        blob = scannerService.base64ToBlob(pdfData.pdfBase64, 'application/pdf')
        extension = 'pdf'
      }

      const url = URL.createObjectURL(blob)

      dispatch(
        setCurrentScanAfterRotation({
          blob,
          url,
          filename: scannerService.generateFilename(extension),
          size: blob.size,
          timestamp: new Date(),
          format: extension,
        }),
      )
      setRotationDegree(0)
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: 'Échec de la rotation',
          type: 'false',
        }),
      )
    }
  }

  const handleSaveScan = async () => {
    if (!currentScan) return
    try {
      if (savePfd && currentScan.blob) {
        const file = new File([currentScan.blob], currentScan.filename, {
          type: currentScan.blob.type,
        })

        savePfd(file)
      }
      dispatch(clearCurrentScan())
    } catch (error) {
      console.error('Save failed:', error)
    }
  }

  const handleDocumentTypeChange = (e: SelectChangeEvent<string>) => {
    setFileType(e.target.value as FileTypeEnum)
  }

  const handleSettingChange = (setting: string, value: any) => {
    console.log(setting, value)
    dispatch(updateScanSettings({ [setting]: value }))
  }

  const handleScannerSelect = (e: SelectChangeEvent<string>) => {
    dispatch(selectScanner(e.target.value))
  }

  useEffect(() => {
    dispatch(
      updateScanSettings({
        dpi: validatedSettings?.dpi,
        colorMode: validatedSettings?.colorMode,
        format: validatedSettings?.format,
        paperSource: validatedSettings?.paperSource,
        pageSize: validatedSettings?.pageSize,
        compression: validatedSettings?.compression,
      }),
    )
  }, [validatedSettings, selectedPageSize, dispatch])

  return (
    <Stack spacing={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Liste des Scanners Détectés</Typography>
        <Box display="flex" gap={1} alignItems={'center'}>
          <TimelineDot color={serviceConnected ? 'success' : 'warning'} />
          <Typography color={serviceConnected ? 'success' : 'warning'}>
            {serviceConnected ? 'Service Connecté' : 'Service Déconnecté'}
          </Typography>
        </Box>
      </Box>
      {(scanError || discoveryError) && (
        <Alert severity="error" onClose={() => dispatch(clearError())}>
          {scanError || discoveryError}
        </Alert>
      )}
      {scanners.length === 0 ? (
        <Box>
          <Alert severity="warning">
            Service de scanner non disponible. Veuillez essayer de brancher
            votre scanner ou de redémarrer le service de scanner.
          </Alert>
        </Box>
      ) : (
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl fullWidth size="small">
            <InputLabel id="scanners-list-label">Scanners Détectés</InputLabel>
            <Select
              labelId="scanners-list-label"
              value={selectedScannerId || ''}
              label="Scanners Détectés"
              onChange={handleScannerSelect}
              size="small"
              disabled={isScanning || scanners.length === 0}
            >
              {scanners.length === 0 ? (
                <MenuItem disabled>
                  {isDiscovering
                    ? 'Recherche en cours...'
                    : 'Aucun scanner détecté'}
                </MenuItem>
              ) : (
                scanners.map((scanner) => (
                  <MenuItem key={scanner.id} value={scanner.id}>
                    {scanner.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={handleRefreshScanners}
            disabled={isDiscovering || isScanning}
            size="large"
            sx={{ minWidth: '120px' }}
          >
            {isDiscovering ? <CircularProgress size={20} /> : 'Actualiser'}
          </Button>
        </Stack>
      )}

      {isScannerCapsLoaded && scannerCaps && (
        <Alert severity="success">
          Capacités détectées du scanner : {scannerCaps.model}
        </Alert>
      )}
      {isScannerCapsLoaded && scannerCaps && (
        <Box
          component="form"
          sx={{ '& .MuiTextField-root': { m: 0, width: '100%' } }}
          noValidate
          autoComplete="off"
        >
          <div>
            <FormControl fullWidth size="small">
              <InputLabel id="document-type-label">Document Type</InputLabel>
              <Select
                id="document-type-label"
                value={fileType || ''}
                label="Document Type"
                disabled={isScanning || scanners.length === 0}
                onChange={handleDocumentTypeChange}
                size="small"
                fullWidth
              >
                {fileoptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    {intl.formatMessage({ id: `enums.FileTypeEnum.${value}` })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isValidating && (
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                Vérification des capacités du scanner...
              </Alert>
            )}
          </div>
        </Box>
      )}

      {settingsWarnings.length > 0 && !isValidating && (
        <Alert
          severity="warning"
          sx={{ '& .MuiAlert-icon': { alignSelf: 'flex-start' } }}
        >
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Paramètres ajustés:
          </Typography>
          {settingsWarnings.map((warning, index) => (
            <Typography key={index} variant="body2">
              • {warning}
            </Typography>
          ))}
        </Alert>
      )}
      {isScannerCapsLoaded && (
        <Box
          component="form"
          sx={{ '& .MuiTextField-root': { m: 0.5, width: '18ch' } }}
          noValidate
          autoComplete="off"
        >
          <div>
            <TextField
              required
              id="dpi-resolution"
              label="dpi Resolution"
              size="small"
              select
              value={currentSettings.dpi}
              onChange={(e) =>
                handleSettingChange('dpi', Number(e.target.value))
              }
              disabled={isScanning}
            >
              {availableOptions?.dpiValues.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              id="color-density"
              label="Color Density"
              size="small"
              select
              value={currentSettings.colorMode}
              onChange={(e) => handleSettingChange('colorMode', e.target.value)}
              disabled={isScanning}
            >
              {availableOptions.ColorModes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              id="paper-source"
              label="Paper Source"
              size="small"
              select
              value={currentSettings.paperSource}
              onChange={(e) =>
                handleSettingChange('paperSource', e.target.value)
              }
              disabled={isScanning}
            >
              {availableOptions.paperSources.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              id="outlined-select-pagesize"
              select
              label="Select Page Size"
              size="small"
              value={selectedPageSize}
              onChange={(e) => handleSettingChange('pageSize', e.target.value)}
              disabled={isScanning}
            >
              {pageSize.map((option) => (
                <MenuItem key={option.id} value={option.size}>
                  {option.size}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </Box>
      )}

      {!currentScan ? (
        <Button
          variant="contained"
          size="medium"
          onClick={handleScan}
          disabled={!selectedScannerId || isScanning}
          sx={{
            height: 'fit-content',
            alignSelf: 'center',
          }}
          startIcon={
            isScanning ? (
              <CircularProgress size={20} color="inherit" />
            ) : undefined
          }
        >
          {isScanning ? 'Numérisation en cours...' : 'Scanner le Document'}
        </Button>
      ) : (
        <Stack spacing={2} direction="row" justifyContent="space-between">
          <Button
            variant="outlined"
            size="small"
            color="success"
            onClick={handleSaveScan}
            sx={{ height: 'fit-content', alignSelf: 'center' }}
          >
            Enregistrer le Fichier
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            onClick={handleClearScan}
            sx={{ height: 'fit-content', alignSelf: 'center' }}
          >
            Effacer & Scanner
          </Button>

          {rotationDegree !== 0 && (
            <Button
              variant="outlined"
              size="small"
              color="success"
              onClick={handleValidateRotation}
              sx={{ height: 'fit-content', alignSelf: 'center' }}
            >
              Valider la rotation
            </Button>
          )}
          <Tooltip title="Faire pivoter le document de 90 degrés à la fois">
            <Button
              variant="outlined"
              size="small"
              color="success"
              onClick={handleRotation}
              sx={{ height: 'fit-content', alignSelf: 'center' }}
            >
              <RotateRight />
            </Button>
          </Tooltip>
        </Stack>
      )}
      {currentScan && (
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Fichier: {currentScan.filename} • Taille:{' '}
            {(currentScan.size / 1024).toFixed(2)} KB
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #ddd',
              borderRadius: 1,
              mt: 1,
              overflow: 'hidden',
              bgcolor: '#f5f5f5',
            }}
          >
            {currentScan.format === 'jpg' ? (
              <img
                src={currentScan.url}
                alt="Document Numérisé"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  rotate: `${rotationDegree}deg`,
                }}
              />
            ) : (
              <iframe
                src={currentScan.url}
                title="Document Numérisé"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  rotate: `${rotationDegree}deg`,
                }}
              />
            )}
          </Box>
        </Box>
      )}
    </Stack>
  )
}

export default DocumentsScanner
